import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  FormControlLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { Form } from "react-bootstrap";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";

dayjs.extend(isSameOrAfter);
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { SuiteApps } from "../../../Helper";
import SearchIcon from "../../../Custom-imeges/SearchIcon";
import { FilterIcon } from "../../../Custom-imeges/FilterIcon";
import { DateIcon } from "../../../Custom-imeges/DateIcon";
import useDebounce from "../../../features/main-page/components/get-conversation/useDebounce";
import { GetOptionsRequest } from "../../../store/Apis/SuitesApps/SuiteHistroyApi";
import { FilterAppItem } from "./SuiteHistoryNavbar";

interface SuiteHistoryFilterProps {
  activeApp: string;
  handleOpenDeleteModal: (canShow: boolean) => void;
  filterApps: FilterAppItem[];
  getTextHistoryItems: (data?: GetOptionsRequest) => void;
  getImageHistoryItems: (data?: GetOptionsRequest) => void;
  getVideoHistoryItems: (data?: GetOptionsRequest) => void;
  searchInputValue: string;
  setSearchInputValue: (search: string) => void;
}

export const SuiteHistoryFilter = ({
  activeApp,
  handleOpenDeleteModal,
  filterApps,
  getTextHistoryItems,
  getImageHistoryItems,
  getVideoHistoryItems,
  searchInputValue,
  setSearchInputValue,
}: SuiteHistoryFilterProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [submenuOne, setSubmenuOne] = useState<null | HTMLElement>(null);
  const [submenuTwo, setSubmenuTwo] = useState<null | HTMLElement>(null);
  const [selectedApps, setSelectedApps] = useState<string[]>([]);
  const [fromDate, setFromDate] = useState<dayjs.Dayjs | null>(null);
  const [toDate, setToDate] = useState<dayjs.Dayjs | null>(null);
  const debouncedSearchTerm = useDebounce(searchInputValue, 1000);
  const open = Boolean(anchorEl);
  const submenuOneOpen = Boolean(submenuOne);
  const submenuTwoOpen = Boolean(submenuTwo);
  const [showSuggestionMenu, setShowSuggestionMenu] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const today = dayjs();

  const areAllSelected =
    filterApps?.length > 0 && selectedApps.length === filterApps.length;

  useEffect(() => {
    switch (activeApp) {
      case SuiteApps.Text:
        getTextHistoryItems({ Search: searchInputValue });
        break;
      case SuiteApps.Image:
        getImageHistoryItems({ Search: searchInputValue });
        break;
      case SuiteApps.Video:
        getVideoHistoryItems({ Search: searchInputValue });
        break;
      default:
        break;
    }
  }, [debouncedSearchTerm]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSubmenuOne(null);
    setSubmenuTwo(null);
  };

  const handleSubmenuOneOpen = (event: React.MouseEvent<HTMLLIElement>) => {
    setSubmenuOne(event.currentTarget);
  };

  const handleSubmenuTwoOpen = (event: React.MouseEvent<HTMLLIElement>) => {
    setSubmenuTwo(event.currentTarget);
  };

  const handleSubmenuOneClose = () => {
    setSubmenuOne(null);
  };

  const handleSubmenuTwoClose = () => {
    setSubmenuTwo(null);
  };

  const handleAppCheckboxChange = (appId: string, checked: boolean) => {
    setSelectedApps((prev) =>
      checked ? [...prev, appId] : prev.filter((id) => id !== appId)
    );
  };

  const handleSelectAllChange = (checked: boolean) => {
    if (checked) {
      setSelectedApps(filterApps.map((app) => app.id));
    } else {
      setSelectedApps([]);
    }
  };

  const handleApplyHistoryFiltration = () => {
    const options = {
      AppIds: selectedApps,
      StartDate: fromDate ? fromDate.format("YYYY-MM-DD") : undefined,
      EndDate: toDate ? toDate.format("YYYY-MM-DD") : undefined,
    };

    switch (activeApp) {
      case SuiteApps.Text:
        getTextHistoryItems(options);
        break;
      case SuiteApps.Image:
        getImageHistoryItems(options);
        break;
      case SuiteApps.Video:
        getVideoHistoryItems(options);
        break;
      default:
        break;
    }
    setAnchorEl(null);
  };

  const handleResetAllFiltration = () => {
    setSelectedApps([]);
    setFromDate(null);
    setToDate(null);
    setAnchorEl(null);
    setSearchInputValue("");
    switch (activeApp) {
      case SuiteApps.Text:
        getTextHistoryItems();
        break;
      case SuiteApps.Image:
        getImageHistoryItems();
        break;
      case SuiteApps.Video:
        getVideoHistoryItems();
        break;
      default:
        break;
    }
  };

  const handleSelectSuggestionItem = (item: string) => {
    setSearchInputValue("");
    setSearchInputValue(item);
    setShowSuggestionMenu(false);
  };

  const handleDateChange = (newDate: dayjs.Dayjs | null, isFrom: boolean) => {
    if (!newDate) return;

    const normalizedDate = isFrom
      ? newDate.startOf("day")
      : newDate.endOf("day");

    if (normalizedDate.isSame(today, "day")) {
      if (isFrom) {
        setFromDate(today.startOf("day"));
        // Adjust toDate if it's before the new fromDate
        if (toDate && today.isAfter(toDate, "day")) {
          setToDate(today.endOf("day"));
        }
      } else {
        if (!fromDate || today.isSameOrAfter(fromDate, "day")) {
          setToDate(today.endOf("day"));
        }
      }
    } else {
      if (isFrom) {
        setFromDate(normalizedDate);
        if (toDate && normalizedDate.isAfter(toDate, "day")) {
          setToDate(normalizedDate.endOf("day"));
        }
      } else {
        if (!fromDate || normalizedDate.isSameOrAfter(fromDate, "day")) {
          setToDate(normalizedDate);
        }
      }
    }
  };

  // Define the start and end of the range for date selection.
  const currentDate = dayjs(today || new Date());
  const startOfLastMonth = currentDate.subtract(1, "month").startOf("month");
  const endOfCurrentMonth = currentDate.endOf("month");

  return (
    <Box className="suite-history-filter">
      <Box className="searchBox suite-history">
        <Form.Label
          className="mb-0"
          htmlFor="historySearch"
          style={{ marginLeft: "5px" }}
        >
          <SearchIcon className="filter-search-icon" />
        </Form.Label>
        <Form.Control
          id="historySearch"
          placeholder="Search for apps"
          ref={searchInputRef}
          value={searchInputValue}
          onFocus={() => setShowSuggestionMenu(true)}
          onChange={(e) => setSearchInputValue(e.target.value)}
        />
        <div className="suite-history-filter-container">
          <Button
            sx={{
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
            id="basic-button"
            aria-controls={open ? "basic-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
            onClick={handleClick}
          >
            <FilterIcon className="custom-icon" />
          </Button>

          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            MenuListProps={{
              "aria-labelledby": "basic-button",
              sx: {
                backgroundColor: "var(--M-background93)",
                color: "var(--Main_icons_color)",
                padding: "16px 12px",
              },
            }}
            sx={{
              "& .MuiPaper-root": {
                background: "var(--M-background93)",
                padding: "0",
              },
            }}
          >
            <Box className="suite-filter-header">
              <Typography>Filter by</Typography>
              <Box
                className="reset-all-suite-history"
                onClick={handleResetAllFiltration}
              >
                Reset All
              </Box>
            </Box>
            <MenuItem className="filter-option" onClick={handleSubmenuOneOpen}>
              <ListItemIcon>
                <ArrowBackIcon
                  sx={{ color: "var(--Main_icons_color)", fontSize: "1.2rem" }}
                />
              </ListItemIcon>
              <ListItemText primary="Apps" />
            </MenuItem>
            <MenuItem className="filter-option" onClick={handleSubmenuTwoOpen}>
              <ListItemIcon>
                <ArrowBackIcon
                  sx={{ color: "var(--Main_icons_color)", fontSize: "1.2rem" }}
                />
              </ListItemIcon>
              <ListItemText primary="Date" />
            </MenuItem>
            <Box className="filter-option-save-options">
              <Box className="cancel-option" onClick={handleClose}>
                Cancel
              </Box>
              <Box
                className="apply-option"
                onClick={handleApplyHistoryFiltration}
              >
                Apply
              </Box>
            </Box>
          </Menu>

          <Menu
            anchorEl={submenuOne}
            open={submenuOneOpen}
            onClose={handleSubmenuOneClose}
            anchorOrigin={{ vertical: "top", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            sx={{
              "& .MuiPaper-root": {
                background: "var(--M-background93)",
                padding: "8px",
                color: "var(--Main_icons_color)",
                borderRadius: "8px",
                boxShadow: "0px 4px 8px 4px rgba(0, 0, 0, 0.25)",
              },
            }}
          >
            {filterApps?.map((app, index) => (
              <MenuItem key={index}>
                <FormControlLabel
                  className="app-option"
                  control={
                    <input
                      type="checkbox"
                      className="checkBox suite-history"
                      checked={selectedApps.includes(app.id)}
                      onChange={(e) =>
                        handleAppCheckboxChange(app.id, e.target.checked)
                      }
                    />
                  }
                  label={app.name}
                />
              </MenuItem>
            ))}

            <Box
              className="select-all-apps-option"
              onClick={() => handleSelectAllChange(!areAllSelected)}
            >
              <input
                type="checkbox"
                className="checkBox suite-history"
                checked={areAllSelected}
                readOnly
              />
              <Typography>Select All</Typography>
            </Box>
          </Menu>

          <Menu
            anchorEl={submenuTwo}
            open={submenuTwoOpen}
            onClose={handleSubmenuTwoClose}
            anchorOrigin={{ vertical: "top", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            sx={{
              "& .MuiPaper-root": {
                background: "var(--M-background126)",
                padding: "8px",
                color: "var(--Main_icons_color)",
                borderRadius: "8px",
                boxShadow: "0px 4px 8px 4px rgba(0, 0, 0, 0.25)",
              },
            }}
          >
            <Box className="suite-history-date-filter">
              <Box className="suite-history-date-filter-from">
                <Box className="suite-history-date-from">
                  <Typography>
                    {fromDate ? fromDate.format("YYYY-MM-DD") : "From"}
                  </Typography>
                  <DateIcon className="custom-icon" />
                </Box>
                <Box className="suite-history-date-filter-from-date">
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateCalendar
                      value={fromDate || today}
                      onChange={(date) => handleDateChange(date, true)}
                      maxDate={endOfCurrentMonth}
                      disableFuture
                      views={["day"]}
                    />
                  </LocalizationProvider>
                </Box>
              </Box>
              <Box className="suite-history-date-filter-to">
                <Box className="suite-history-date-to">
                  <Typography>
                    {toDate ? toDate.format("YYYY-MM-DD") : "To"}
                  </Typography>
                  <DateIcon className="custom-icon" />
                </Box>
                <Box className="suite-history-date-filter-to-date">
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateCalendar
                      value={toDate || today}
                      onChange={(date) => handleDateChange(date, false)}
                      maxDate={endOfCurrentMonth}
                      disableFuture
                      views={["day"]}
                    />
                  </LocalizationProvider>
                </Box>
              </Box>
            </Box>
          </Menu>
        </div>
        <Box
          className={`suggestion-search-result ${
            searchInputValue.length > 0 && showSuggestionMenu && !anchorEl
              ? "active"
              : ""
          }`}
        >
          {filterApps
            ?.filter((app) =>
              app.name.toLowerCase().includes(searchInputValue.toLowerCase())
            )
            ?.map((filteredApp) => (
              <Box
                key={filteredApp.id}
                className="suggestion-item"
                onClick={() => handleSelectSuggestionItem(filteredApp.name)}
              >
                {filteredApp.name}
              </Box>
            ))}
          {filterApps?.filter((app) =>
            app.name.toLowerCase().includes(searchInputValue.toLowerCase())
          ).length === 0 && (
            <Box className="no-result-item">No Result For Your Search</Box>
          )}
        </Box>
      </Box>
      <Box
        className="clear-all-suite-history"
        onClick={() => handleOpenDeleteModal(true)}
      >
        Clear All History
      </Box>
    </Box>
  );
};
